import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import cookies from "js-cookie";
import { safeReturnTo } from './utils';
import cmsaxios from '../../../utils/cms-axios';
import { onSessionExpired } from '../../../utils/session-expired';
import { AuthContext } from './auth-context';
import { useSearchParams } from '../../../routes/hook';
import { useGetUser } from '../../../cms-api/user';

const initialState = {
  user: null,
  loading: true,
};

const reducer = (state, action) => {
  if (action.type === 'INITIAL') {
    return {
      loading: false,
      user: action.payload.user,
    };
  }
  if (action.type === 'LOGIN') {
    return {
      ...state,
      user: action.payload.user,
    };
  }
  if (action.type === 'REGISTER') {
    return {
      ...state,
      user: action.payload.user,
    };
  }
  if (action.type === 'LOGOUT') {
    return {
      ...state,
      user: null,
    };
  }
  return state;
};

// /api/user の失敗が「未ログイン」か。
// BFF（cms-client-admin の ApiProxy）は未ログインを 401 の JSON（error.not_login）で返す。
// 古い BFF は 302 で / へ飛ばすので、XHR は SPA の HTML（JSON でない）を受け取る。どちらも未ログイン。
const isNotLoginResult = (data) =>
  !data || typeof data !== 'object' || !!data.error?.not_login;
// catch に来た値（応答本文、または通信エラーの文字列）が 401 の未ログインか。
// 通信エラーは未ログインと区別してログに残す。
const isNotLoginError = (error) =>
  !!error && typeof error === 'object' && !!error.error?.not_login;

export function AuthProvider({ children }) {

  const [state, dispatch] = useReducer(reducer, initialState);

  const initialize = useCallback(async () => {
    // ログイン中かは /api/user の応答で決める。
    // ⚠ api_key Cookie を JS で読んで判定しないこと。HttpOnly なので読めない
    //   （cms-client-admin の login.go Callback）。
    const setUser = (user) => dispatch({ type: 'INITIAL', payload: { user } });
    try {
      const response = await cmsaxios.get('/api/user');

      if (response.data?.status === "OK") {

        setUser(response.data);

        // 保存時にも検証しているが、Cookie は書き換えられるので読むときも検証する。
        const returnTo = cookies.get('returnTo');

        if (returnTo) {
          cookies.remove("returnTo")
          const safe = safeReturnTo(returnTo);
          if (safe) {
            window.location.replace(safe);
          }
        }
      } else {
        if (!isNotLoginResult(response.data)) console.error(response.data.error);
        setUser(null);
      }
    } catch (error) {
      // cms-axios のインターセプタは失敗時に応答本文をそのまま投げる。
      if (!isNotLoginError(error)) console.error(error);
      setUser(null);
    }
  }, []);

  useEffect(() => {
    initialize();
  }, [initialize]);

  // LOGIN
  const login = useCallback(async (appState) => {
    const response = await cmsaxios.get('/login');
    if (response.data.status !== 'OK') {
      throw new Error(response.data);
    }
    // 戻り先は同一オリジンの相対パスだけ（safeReturnTo）。不正な値は捨てる。
    const returnTo = safeReturnTo(appState?.returnTo);
    if (returnTo) {
      cookies.set("returnTo", returnTo)
    } else {
      cookies.remove("returnTo")
    }
    window.location.href = response.data.url;
  }, []);

  // LOGOUT
  const logout = useCallback(async () => {
    // POST のみ (BFF は GET の /logout を 405 で断る。リンク 1 つで強制ログアウトさせられないように)
    const response = await cmsaxios.post('/logout');
    if (response.data.status !== 'OK') {
      throw new Error(response.data);
    }

    window.location.href = response.data.url;
  }, []);

  // ---- 使用中のログイン切れ ----
  // cms-axios が BFF の 401（error.not_login）を見たら知らせてくる（utils/session-expired.js）。
  // ログイン中に起きたときだけ案内を出す（ログイン画面での /api/user の 401 は対象外）。
  // ⚠ ここでログイン画面へ飛ばさないこと。編集中の入力が消える。案内（SessionExpiredDialog）を
  //   出して、再ログインするかはユーザーに選ばせる。
  const [sessionExpired, setSessionExpired] = useState(false);
  const userRef = useRef(null);
  useEffect(() => { userRef.current = state.user; }, [state.user]);
  useEffect(() => onSessionExpired(() => {
    if (userRef.current) setSessionExpired(true);
  }), []);

  const dismissSessionExpired = useCallback(() => setSessionExpired(false), []);

  // 再ログインして今の画面に戻る（戻り先は safeReturnTo で検証される）。
  const relogin = useCallback(
    () => login({ returnTo: `${window.location.pathname}${window.location.search}` }),
    [login]
  );

  const checkAuthenticated = state.user ? 'authenticated' : 'unauthenticated';

  const status = state.loading ? 'loading' : checkAuthenticated;

  const memoizedValue = useMemo(
    () => ({
      user: state.user,
      method: 'cms',
      loading: status === 'loading',
      authenticated: status === 'authenticated',
      unauthenticated: status === 'unauthenticated',
      //
      login,
      logout,
      //
      sessionExpired,
      dismissSessionExpired,
      relogin,
    }),
    [login, logout, state.user, status, sessionExpired, dismissSessionExpired, relogin]
  );

  return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>;
}

AuthProvider.propTypes = {
  children: PropTypes.node,
};